// ═══════════════════════════════════════════════════════════════════════════
// CreditBalance — persistent header indicator + popover.
//
// Pill shows the current total (monthly + top-up). Click opens a popover with:
//   • Current balance breakdown (monthly / top-up)
//   • Next refill date (when known)
//   • Recent 5 transactions
//   • "Buy more credits" CTA (or "Upgrade" for free tier)
//
// Subscribes to a custom event so other parts of the app can ping it to
// re-fetch after a deduct/refund/top-up:
//
//   window.dispatchEvent(new Event('credits:changed'))
//
// This avoids prop-drilling through the whole tree.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { getUserCredits, getRecentTransactions } from '../../lib/credits';

export default function CreditBalance({ onBuyTopup, onUpgrade }) {
  const { user, profile } = useAuth();
  const [open, setOpen]             = useState(false);
  const [credits, setCredits]       = useState(null);
  const [transactions, setTxs]      = useState([]);
  const [loading, setLoading]       = useState(true);
  const anchorRef                   = useRef(null);
  const popoverRef                  = useRef(null);

  const refresh = useCallback(async () => {
    if (!user?.id) { setCredits(null); setTxs([]); setLoading(false); return; }
    try {
      const [row, txs] = await Promise.all([
        getUserCredits(user.id),
        getRecentTransactions(user.id, 5),
      ]);
      setCredits(row);
      setTxs(txs);
    } catch (err) {
      console.error('CreditBalance refresh error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Other components fire this event after a deduct/refund to trigger refresh.
  useEffect(() => {
    function handler() { refresh(); }
    window.addEventListener('credits:changed', handler);
    return () => window.removeEventListener('credits:changed', handler);
  }, [refresh]);

  // Close the popover on outside-click or Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (!popoverRef.current || !anchorRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (anchorRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const nextRefill = useMemo(() => {
    if (!credits?.last_refill_at) return null;
    const next = new Date(new Date(credits.last_refill_at).getTime() + 30 * 24 * 60 * 60 * 1000);
    if (isNaN(next.getTime())) return null;
    return next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [credits?.last_refill_at]);

  if (!user) return null;

  const balance      = credits?.balance ?? 0;
  const topup        = credits?.topup_balance ?? 0;
  const total        = balance + topup;
  const isFreeTier   = (profile?.plan || 'free') === 'free';
  const indicatorTone = total <= 0 ? 'empty' : total < 30 ? 'low' : 'ok';

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...styles.pill,
          ...(indicatorTone === 'empty' ? styles.pillEmpty : {}),
          ...(indicatorTone === 'low'   ? styles.pillLow   : {}),
        }}
        aria-haspopup="true"
        aria-expanded={open}
        title="Credits"
      >
        <SparkIcon />
        <span style={{ fontWeight: 600 }}>
          {loading ? '—' : total.toLocaleString()}
        </span>
        <span style={styles.pillLabel}>credits</span>
      </button>

      {open && (
        <div ref={popoverRef} style={styles.popover} role="dialog" aria-label="Credit balance">
          <div style={styles.popoverHeader}>
            <div style={styles.popoverTitle}>Your credits</div>
            <button
              onClick={() => setOpen(false)}
              style={styles.closeBtn}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Big total */}
          <div style={styles.totalRow}>
            <div style={styles.totalNumber}>{total.toLocaleString()}</div>
            <div style={styles.totalLabel}>available</div>
          </div>

          {/* Breakdown */}
          <div style={styles.breakdown}>
            <div style={styles.breakdownRow}>
              <span style={styles.breakdownLabel}>Monthly</span>
              <span style={styles.breakdownValue}>{balance.toLocaleString()}</span>
            </div>
            <div style={styles.breakdownRow}>
              <span style={styles.breakdownLabel}>Top-up (never expires)</span>
              <span style={styles.breakdownValue}>{topup.toLocaleString()}</span>
            </div>
            {nextRefill && (
              <div style={styles.breakdownRow}>
                <span style={styles.breakdownLabel}>Next refill</span>
                <span style={styles.breakdownValue}>{nextRefill}</span>
              </div>
            )}
          </div>

          {/* Recent activity */}
          {transactions.length > 0 && (
            <>
              <div style={styles.sectionLabel}>Recent activity</div>
              <div style={styles.txList}>
                {transactions.map((tx) => (
                  <TxRow key={tx.id} tx={tx} />
                ))}
              </div>
            </>
          )}

          {/* CTAs */}
          <div style={styles.ctaRow}>
            <button
              onClick={() => { setOpen(false); onBuyTopup?.(); }}
              style={styles.primaryCta}
            >
              Buy more credits
            </button>
            {isFreeTier && (
              <button
                onClick={() => { setOpen(false); onUpgrade?.(); }}
                style={styles.secondaryCta}
              >
                Upgrade to Creator
              </button>
            )}
          </div>

          {/* Transparency strip */}
          <div style={styles.transparencyNote}>
            Credits roll over up to 2–3× on paid plans. Top-ups never expire.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-rows ─────────────────────────────────────────────────────────────

function TxRow({ tx }) {
  const isCredit = tx.amount > 0;
  const sign = isCredit ? '+' : '';
  const color = isCredit ? 'var(--gold, #ffb101)' : 'rgba(249,240,230,0.8)';
  const label = formatTxLabel(tx);
  const when = formatRelative(tx.created_at);

  return (
    <div style={styles.txRow}>
      <div style={styles.txLabel}>
        <span>{label}</span>
        <span style={styles.txTime}>{when}</span>
      </div>
      <div style={{ ...styles.txAmount, color }}>
        {sign}{tx.amount}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatTxLabel(tx) {
  const byType = {
    welcome_bonus:   'Welcome bonus',
    monthly_refill:  'Monthly refill',
    daily_overflow:  'Daily top-up',
    topup_purchase:  'Credit purchase',
    refund:          'Refund',
    referral:        'Referral bonus',
    rollover:        'Rollover',
    adjustment:      'Adjustment',
  };
  if (byType[tx.type]) return byType[tx.type];
  if (tx.type === 'usage' && tx.action) {
    return actionToLabel(tx.action);
  }
  return tx.action || tx.type;
}

function actionToLabel(action) {
  const map = {
    episode_build_simple:   'Episode build',
    episode_build_advanced: 'Advanced build',
    episode_section_regen:  'Section regen',
    cover_art_imagen_fast:  'Cover art',
    cover_art_flux_pro:     'Cover art (pro)',
    thumbnail_ideogram:     'Thumbnail',
    ai_show_notes:          'Show notes',
    viral_clip_prediction:  'Clip prediction',
    episode_remix:          'Episode remix',
    trend_deep_report:      'Trend report',
    legacy_consume:         'Credit use',
  };
  return map[action] || action.replace(/_/g, ' ');
}

function formatRelative(iso) {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SparkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l2.6 7.4L22 12l-7.4 2.6L12 22l-2.6-7.4L2 12l7.4-2.6z" />
    </svg>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────

const styles = {
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid rgba(255,177,1,0.25)',
    background: 'rgba(255,177,1,0.06)',
    color: 'var(--gold, #ffb101)',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    lineHeight: 1,
  },
  pillLow: {
    borderColor: 'rgba(245,158,11,0.35)',
    background: 'rgba(245,158,11,0.08)',
    color: '#f59e0b',
  },
  pillEmpty: {
    borderColor: 'rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.08)',
    color: '#ef4444',
  },
  pillLabel: {
    color: 'rgba(249,240,230,0.55)',
    fontSize: 12,
  },

  popover: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    width: 340,
    padding: 16,
    borderRadius: 14,
    background: 'var(--panel, #141010)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
    zIndex: 100,
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  popoverTitle: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 16,
    color: 'var(--text, #f9f0e6)',
  },
  closeBtn: {
    width: 24,
    height: 24,
    borderRadius: 999,
    border: 'none',
    background: 'transparent',
    color: 'rgba(249,240,230,0.5)',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
  },

  totalRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 14,
  },
  totalNumber: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 36,
    fontWeight: 500,
    color: 'var(--gold, #ffb101)',
    lineHeight: 1,
  },
  totalLabel: {
    fontSize: 13,
    color: 'rgba(249,240,230,0.5)',
  },

  breakdown: {
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.05)',
    marginBottom: 14,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 13,
    padding: '4px 0',
  },
  breakdownLabel: {
    color: 'rgba(249,240,230,0.55)',
  },
  breakdownValue: {
    color: 'var(--text, #f9f0e6)',
    fontWeight: 500,
  },

  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(249,240,230,0.4)',
    marginBottom: 8,
  },
  txList: {
    marginBottom: 14,
  },
  txRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  txLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    fontSize: 13,
  },
  txTime: {
    fontSize: 11,
    color: 'rgba(249,240,230,0.4)',
  },
  txAmount: {
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },

  ctaRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  primaryCta: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 999,
    border: '1px solid transparent',
    background: 'linear-gradient(180deg, #e95a28, var(--orange, #ce3906))',
    color: 'white',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryCta: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 999,
    border: '1px solid rgba(255,177,1,0.35)',
    background: 'transparent',
    color: 'var(--gold, #ffb101)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },

  transparencyNote: {
    fontSize: 11,
    color: 'rgba(249,240,230,0.35)',
    textAlign: 'center',
    lineHeight: 1.4,
  },
};
